import type { GarmentJob } from '../api/garmentJobs';
import type { GarmentJobKind, GarmentJobStatus } from '../generated/enums';
import { humanizeEnum } from '../format/text';
import type { StatusTone } from '../vocabulary';

/**
 * What a counter can do to a job in this state.
 *
 * <p>
 * Mirrors the service's own refusals, and the duplication is the deliberate kind `garmentMoves.ts`
 * already carries: a screen that offers a button the server will refuse has moved the error from
 * before the click to after it, and on a counter that is the difference between a clerk correcting
 * a selection and a clerk staring at a toast. <strong>The server remains authoritative</strong> — if
 * these ever disagree, this is the one that is wrong.
 * </p>
 */
export interface JobActions {
  dispatch: boolean;
  receive: boolean;
  complete: boolean;
  /**
   * Back from the tailor, ready — the booking's own move, sent from the queue. Offered exactly
   * where `complete` is withheld: an alteration a booking owns.
   */
  ready: boolean;
  reClean: boolean;
  damaged: boolean;
  extend: boolean;
}

const closed: GarmentJobStatus[] = ['Completed', 'DamagedInJob'];

/**
 * With somebody outside the shop — what a receive is legal from, and the queue's "out". A garment
 * sent round again is out again, so it can come back again.
 */
export function isOut(job: GarmentJob): boolean {
  return job.status === 'Dispatched' || job.status === 'ReCleanRequired';
}

/**
 * An alteration raised by a booking, as against one somebody raised by hand on the rail.
 *
 * <p>
 * The two are the same row and close in different places. A cleaning job also carries a booking, so
 * the kind is half the test: its garment came off a finished booking and has no state left to move.
 * </p>
 */
function isBookingOwnedAlteration(job: GarmentJob): boolean {
  return job.kind === 'Alteration' && job.rentalOrderItemId !== null;
}

/**
 * Who has it, in the three states that are all real: an outside vendor, one of the shop's own
 * people by name, or the shop with nobody named — a one-karigar shop has nobody to name.
 */
export function withWhom(job: GarmentJob): string {
  if (job.vendorName) {
    return `With ${job.vendorName}`;
  }

  return job.employeeName ? `In the shop — ${job.employeeName}` : 'In the shop';
}

/**
 * What "back — ready" does, said under the button: the booking's own move, so the garment's line,
 * its block and this job all close together.
 */
export function readyHint(job: GarmentJob): string {
  const booking = job.bookingNumber ? `booking ${job.bookingNumber}` : 'its booking';

  return `Marks it ready on ${booking} — the garment comes off the tailor’s dates and this job closes with it.`;
}

export function actionsFor(job: GarmentJob): JobActions {
  if (closed.includes(job.status)) {
    return {
      dispatch: false,
      receive: false,
      complete: false,
      ready: false,
      reClean: false,
      damaged: false,
      extend: false,
    };
  }

  const ownedByABooking = isBookingOwnedAlteration(job);

  return {
    dispatch: job.status === 'Pending',

    receive: isOut(job),

    // Inhouse work goes Pending → Completed by being *done*; there is nobody to receive it from.
    //
    // **Except an alteration that belongs to a booking, which is finished from the booking.** The
    // server refuses it here, and the reason is not bureaucratic: finishing from this queue releases
    // the block while the garment's own line still reads `Alteration`, so the calendar would offer a
    // garment the booking believes is at the tailor. `ready` sends the booking's own move instead,
    // which does both.
    complete: !ownedByABooking,
    ready: ownedByABooking && job.rentalOrderId !== null,

    // Back, or still marked out — "it came back and it is not right" needs no receive first. A
    // garment that never went anywhere cannot come back dirty.
    reClean: job.status === 'Received' || job.status === 'Dispatched',

    damaged: true,
    extend: true,
  };
}

/**
 * Why this selection cannot go out on one challan, in the order a clerk would fix them.
 *
 * <p>
 * Empty means the dispatch would be accepted. The three rules are the server's: something to send,
 * all of it pending, and all of it one kind — a vendor asked to both wash and alter on one document
 * cannot price it.
 * </p>
 */
export function dispatchProblems(jobs: readonly GarmentJob[]): string[] {
  const problems: string[] = [];

  if (jobs.length === 0) {
    return ['Choose at least one garment.'];
  }

  const notPending = jobs.filter((job) => job.status !== 'Pending');

  if (notPending.length > 0) {
    problems.push(
      `${notPending.length} of these have already gone out. Take them off the list, or receive them first.`,
    );
  }

  // Work one of our own people has stays in the shop: there is nobody to hand a challan to.
  const inHouse = jobs.filter((job) => job.employeeId !== null).length;

  if (inHouse > 0) {
    problems.push(
      `${inHouse} of these ${inHouse === 1 ? 'is' : 'are'} with your own staff, so ${inHouse === 1 ? 'it stays' : 'they stay'} in the shop. Finish ${inHouse === 1 ? 'it' : 'them'} here when done.`,
    );
  }

  if (new Set(jobs.map((job) => job.kind)).size > 1) {
    problems.push('One challan cannot mix cleaning and alterations. Send them separately.');
  }

  return problems;
}

/** Why this selection cannot be taken back in. Only something that went out can come back. */
export function receiveProblems(jobs: readonly GarmentJob[]): string[] {
  if (jobs.length === 0) {
    return ['Choose at least one garment.'];
  }

  const notOut = jobs.filter((job) => !isOut(job));

  return notOut.length > 0
    ? [`${notOut.length} of these are not out with anybody. Only a dispatched garment comes back.`]
    : [];
}

/**
 * Why this selection cannot come back *and* be finished in one press — everything a receive refuses,
 * and a booking's alteration, which the server refuses for the whole selection: the booking closes
 * its own, and each has its own "back — ready".
 */
export function backProblems(jobs: readonly GarmentJob[]): string[] {
  const problems = receiveProblems(jobs);
  const onABooking = jobs.filter(isBookingOwnedAlteration).length;

  if (onABooking > 0) {
    problems.push(
      `${onABooking} of these ${onABooking === 1 ? 'is' : 'are'} on a booking. Use “Back — ready” on each — that hands it back to the booking too.`,
    );
  }

  return problems;
}

export interface QueueCounts {
  waiting: number;
  out: number;
  back: number;
  overdue: number;
}

/**
 * The four numbers the heading reads out.
 *
 * <p>
 * `overdue` is counted from the server's own flag rather than by comparing dates here — see
 * `GarmentJobDto.IsOverdue`, which is resolved against the shop's date rather than the till's.
 * It deliberately overlaps `out`: an overdue garment is still out, and presenting them as disjoint
 * would make the two figures fail to add up to the list a clerk is looking at.
 * </p>
 */
export function counts(jobs: readonly GarmentJob[]): QueueCounts {
  return {
    waiting: jobs.filter((job) => job.status === 'Pending').length,
    out: jobs.filter(isOut).length,
    back: jobs.filter((job) => job.status === 'Received').length,
    overdue: jobs.filter((job) => job.isOverdue).length,
  };
}

/**
 * The queue in one line — the phone's More row and the screen's band: only the figures that are not
 * nothing, and a plain sentence when every garment is on the rail.
 */
export function queueSummary(tally: QueueCounts): string {
  const parts = [
    tally.waiting > 0 ? `${tally.waiting} waiting` : '',
    tally.out > 0 ? `${tally.out} out` : '',
    tally.back > 0 ? `${tally.back} back` : '',
    tally.overdue > 0 ? `${tally.overdue} late` : '',
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : 'Nothing off the rail';
}

/**
 * The stage in a word or two, and the tone it is drawn in — the website's badge and the phone's
 * chip. Damage is the only bad news; going round again needs attention; done is good.
 */
export function jobStage(job: GarmentJob): { label: string; tone: StatusTone } {
  switch (job.status) {
    case 'DamagedInJob':
      return { label: 'Damaged', tone: 'negative' };
    case 'Completed':
      return { label: 'Done', tone: 'positive' };
    case 'ReCleanRequired':
      return { label: 'Going again', tone: 'warning' };
    case 'Pending':
      return { label: 'Waiting', tone: 'neutral' };
    default:
      return { label: humanizeEnum(job.status), tone: 'brand' };
  }
}

export interface QueueSections {
  /** Not gone out yet — sent from here, or finished in the shop. */
  waiting: GarmentJob[];
  /** With a vendor, including one sent round again. */
  out: GarmentJob[];
  /** Back in, waiting to be finished. */
  back: GarmentJob[];
  /** Finished or damaged — shown only when asked. */
  closed: GarmentJob[];
}

/**
 * The queue in the three places a garment can be while it is off the rail, the late ones first in
 * each — the order a clerk chases them in. Counted by the same states {@link counts} reads, so the
 * figures over a section add up to the rows under it.
 */
export function queueSections(jobs: readonly GarmentJob[]): QueueSections {
  const lateFirst = (list: GarmentJob[]) =>
    [...list].sort(
      (a, b) =>
        Number(b.isOverdue) - Number(a.isOverdue) ||
        (a.dueOn ?? '9999').localeCompare(b.dueOn ?? '9999'),
    );

  return {
    waiting: lateFirst(jobs.filter((job) => job.status === 'Pending')),
    out: lateFirst(jobs.filter(isOut)),
    back: lateFirst(jobs.filter((job) => job.status === 'Received')),
    closed: jobs.filter((job) => closed.includes(job.status)),
  };
}

/** What this screen is called, so one component can serve both menu keys. */
export function kindLabel(kind: GarmentJobKind): { title: string; verb: string; vendor: string } {
  return kind === 'Cleaning'
    ? { title: 'Cleaning', verb: 'cleaning', vendor: 'launderer' }
    : kind === 'Repair'
      ? { title: 'Repairs', verb: 'repair', vendor: 'tailor' }
      : { title: 'Alterations', verb: 'alteration', vendor: 'tailor' };
}
